import { IS_INDIAN_EXCHANGE, UNIT_OF_ASSETS_QUANTITY } from 'constants/constants';
import { CURRENCY_STYLE } from 'constants/currency';
import { CURRENCY_TYPE } from 'constants/enums';
import { ContractType } from 'types/IProducts';

import { cropAfterDecimals } from './utils';

/**   @description function to add currency symbol to value */
const formatFiat = (
  currency: string = 'INR',
  amount: number | string = '0',
  locale: string = 'en',
  options: Intl.NumberFormatOptions = {}
) => {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...options,
  }).format(Number(amount));
};

/**
 * Formats in terms of K,L,C
 *
 * @example
 * ```
 *  80000 -> ₹80K
 * ```
 */
const prettifyINR = (amount: number | string, options: Intl.NumberFormatOptions = {}) => {
  return Intl.NumberFormat('en-IN', {
    notation: 'compact',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
    currency: CURRENCY_TYPE.INR,
    ...options,
  })
    .format(Number(amount))
    .replace('T', 'K');
};

const prettifyUSD = (amount: number | string, options: Intl.NumberFormatOptions = {}) => {
  return Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
    currency: CURRENCY_TYPE.USD,
    ...options,
  }).format(Number(amount));
};

const getDefaultCurrencyForNewUser = (primaryCurrency: string) => {
  return IS_INDIAN_EXCHANGE ? UNIT_OF_ASSETS_QUANTITY : primaryCurrency;
};

const DEFAULT_MAX_PRECISION = 2;
const DEFAULT_MIN_PRECISION = 2;

const formatValue = (value: number, options: Intl.NumberFormatOptions = {}) => {
  const { currency, maximumFractionDigits, minimumFractionDigits, ...restOptions } =
    options;

  const minPrecsision = Number.isInteger(value)
    ? 0
    : minimumFractionDigits ?? DEFAULT_MIN_PRECISION;
  const maxPrecision = maximumFractionDigits ?? DEFAULT_MAX_PRECISION;
  const croppedValue = cropAfterDecimals(value, maxPrecision);

  if (!currency) {
    return croppedValue.toString();
  }

  const formattingOptions = {
    style: 'currency',
    minimumFractionDigits: minPrecsision,
    maximumFractionDigits: maxPrecision,
    ...restOptions,
  };

  if (currency === CURRENCY_STYLE.INDIAN) {
    return prettifyINR(croppedValue, formattingOptions);
  }

  if (currency === CURRENCY_STYLE.USD || currency === 'USDT') {
    return prettifyUSD(croppedValue, formattingOptions);
  }

  return formatFiat(currency, croppedValue, 'en', formattingOptions);
};

/**
 * Post-processes a formatted INR amount to display Indian unit words
 * (thousand / lac / crore).
 *
 * - Works with BOTH Intl notations:
 *   - `compact`  → ₹1K / ₹1L / ₹1Cr
 *   - `standard` → ₹1,000 / ₹1,00,000
 * - Does NOT change number rounding logic (Intl remains the source of truth)
 * - Intended to be used AFTER `prettifyINR`
 *
 * Examples:
 *   ₹1.00L      → ₹1 lac
 *   ₹2.50Cr     → ₹2.5 crore
 *   ₹1,00,000   → ₹1 lac
 */

const formatINRWithIndianUnits = (
  formattedAmount: string,
  originalAmount: number | string
) => {
  const value = Number(originalAmount);

  // COMPACT output: ₹1L / ₹1Cr / ₹1K
  if (/[KLC]r?\b/.test(formattedAmount)) {
    return formattedAmount
      .replace(/\.00(?=\s?(K|L|Cr)\b)/, '')
      .replace(/K\b/, ' thousand')
      .replace(/L\b/, ' lac')
      .replace(/Cr\b/, ' crore');
  }

  // STANDARD output: ₹1,00,000
  if (value >= 1e7) return `₹${value / 1e7} crore`;
  if (value >= 1e5) return `₹${value / 1e5} lac`;
  if (value >= 1e3) return `₹${value / 1e3} thousand`;

  return formattedAmount;
};

const isINRQuotedProduct = (product: {
  quotingAssetCurrency?: string;
  contract_type?: string;
}) =>
  product.quotingAssetCurrency === CURRENCY_STYLE.INDIAN &&
  IS_INDIAN_EXCHANGE &&
  product.contract_type === ContractType.Spot;

export {
  formatFiat,
  prettifyINR,
  prettifyUSD,
  getDefaultCurrencyForNewUser,
  formatValue,
  formatINRWithIndianUnits,
  isINRQuotedProduct,
};
